import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import {
  RiLogoutCircleLine,
  RiSettings4Line,
  RiUser3Line,
} from '@/shared/icons/remix'
import {
  providerProfileMenuItemSx,
  providerProfileMenuLabelSx,
  providerProfileMenuListSx,
  providerProfileMenuPaperSx,
  providerProfileVerifyBannerBtnSx,
  providerProfileVerifyBannerSx,
  providerProfileVerifyBannerTextSx,
} from '@/layouts/styles/providerProfileMenuStyles'

type ProviderProfileMenuProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onProfile: () => void
  onSettings: () => void
  onLogout: () => void
  logoutDisabled?: boolean
  /** When true, first row is an eKYC verify banner (Unverified only). */
  showVerifyBanner?: boolean
  onVerifyNow?: () => void
}

type MenuRowProps = {
  label: string
  icon: ReactNode
  testId: string
  onClick: () => void
  disabled?: boolean
}

function MenuRow({
  label,
  icon,
  testId,
  onClick,
  disabled,
}: Readonly<MenuRowProps>) {
  return (
    <Box component="li">
      <Box
        component="button"
        type="button"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        sx={providerProfileMenuItemSx}
      >
        {icon}
        <Typography component="span" sx={providerProfileMenuLabelSx}>
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

export function ProviderProfileMenu({
  anchorEl,
  open,
  onClose,
  onProfile,
  onSettings,
  onLogout,
  logoutDisabled = false,
  showVerifyBanner = false,
  onVerifyNow,
}: Readonly<ProviderProfileMenuProps>) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      id="provider-nav-profile-menu"
      data-testid="provider-nav-profile-menu"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            ...providerProfileMenuPaperSx,
            width: showVerifyBanner ? 240 : providerProfileMenuPaperSx.width,
          },
        },
      }}
    >
      <Box component="ul" sx={providerProfileMenuListSx} aria-label="Account menu">
        {showVerifyBanner ? (
          <Box
            component="li"
            sx={{ listStyle: 'none', px: '12px', pb: '8px' }}
            data-testid="provider-nav-ekyc-banner"
          >
            <Box sx={providerProfileVerifyBannerSx}>
              <Typography sx={providerProfileVerifyBannerTextSx}>
                Verify your account to unlock more survey opportunities.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                data-testid="provider-nav-ekyc-verify"
                onClick={() => {
                  onClose()
                  onVerifyNow?.()
                }}
                sx={providerProfileVerifyBannerBtnSx}
              >
                Verify Me
              </Button>
            </Box>
          </Box>
        ) : null}

        <MenuRow
          label="Profile"
          testId="provider-nav-profile"
          icon={<RiUser3Line size={24} aria-hidden />}
          onClick={onProfile}
        />
        <MenuRow
          label="Settings"
          testId="provider-nav-settings"
          icon={<RiSettings4Line size={24} aria-hidden />}
          onClick={onSettings}
        />
        <MenuRow
          label="Logout"
          testId="provider-nav-logout"
          icon={<RiLogoutCircleLine size={24} aria-hidden />}
          onClick={onLogout}
          disabled={logoutDisabled}
        />
      </Box>
    </Popover>
  )
}
